import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import studentService from "../../services/studentService";
import PrintInstructions from "../../components/common/PrintInstructions";
import StudentIdentityCard from "../../components/StudentIdentityCard";

export default function StudentIdCardPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isClerk } = useApp();
  const basePath = isClerk ? "/clerk/students" : "/students";

  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTemplate, setShowTemplate] = useState(false);
  const [templateStatus, setTemplateStatus] = useState({
    front: true,
    back: true,
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await studentService.getById(id);
        setStudent(res.data);
      } catch (err) {
        toast.error(err.message);
        navigate(basePath);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate, basePath]);

  const handlePrint = () => {
    setTimeout(() => window.print(), 150);
  };

  if (loading) {
    return <div className="loading-center"><div className="spinner" />Loading identity card...</div>;
  }

  if (!student) return null;

  const templatesAvailable = templateStatus.front && templateStatus.back;

  return (
    <div>
      <div className="no-print" style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem", flexWrap: "wrap" }}>
        <button className="btn btn-outline" onClick={() => navigate(`${basePath}/${id}`)}>Back</button>

        <div style={{ flex: 1 }}>
          <h1 className="page-title">Identity Card - {student.fullName}</h1>
          <p className="page-subtitle">
            {student.enrollmentNo}
            {(student.course?.shortName || student.course?.courseName) && ` - ${student.course?.shortName || student.course?.courseName} ${student.yearNumber ? `Year ${student.yearNumber}` : ""}`}
          </p>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontSize: 13, color: "var(--text-muted)", cursor: "pointer", userSelect: "none" }}>
          <input
            type="checkbox"
            checked={showTemplate && templatesAvailable}
            disabled={!templatesAvailable}
            onChange={(e) => setShowTemplate(e.target.checked)}
          />
          Show background reference
        </label>

        <button className="btn btn-outline" onClick={() => navigate(`${basePath}/${id}/edit`)}>Edit</button>
        <button className="btn btn-success" onClick={handlePrint}>Print ID Card</button>
      </div>

      <PrintInstructions />

      <div className="no-print" style={{ background: "#e8f1f8", border: "1px solid #93c5fd", borderRadius: 8, padding: "0.65rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#1e3a8a" }}>
        This page prints the front and back side together. Set the print dialog to <strong>Margins - None</strong> and scale to <strong>100%</strong>.
      </div>

      <div className="no-print" style={{ background: templatesAvailable ? "#f0fdf4" : "#fff7ed", border: `1px solid ${templatesAvailable ? "#86efac" : "#fdba74"}`, borderRadius: 8, padding: "0.65rem 1rem", marginBottom: "1rem", fontSize: 13, color: templatesAvailable ? "#166534" : "#9a3412" }}>
        {templatesAvailable
          ? "Background reference is available at /id-front.jpg and /id-back.jpg. Toggle it on to compare front and back alignment."
          : "One or both ID-card background files were not found. Keep id-front.jpg and id-back.jpg inside client/public to preview both sides."}
      </div>

      <div
        style={{ display: "flex", justifyContent: "center", padding: "0.5rem 0 1.5rem" }}
        onErrorCapture={(event) => {
          const target = event.target;
          if (target?.tagName === "IMG" && target.getAttribute("src") === "/id-front.jpg") {
            setTemplateStatus((prev) => ({ ...prev, front: false }));
            setShowTemplate(false);
          }
          if (target?.tagName === "IMG" && target.getAttribute("src") === "/id-back.jpg") {
            setTemplateStatus((prev) => ({ ...prev, back: false }));
            setShowTemplate(false);
          }
        }}
      >
        <StudentIdentityCard
          student={student}
          showTemplate={showTemplate}
        />
      </div>
    </div>
  );
}
